import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

const PRESETS = [
  { label: "Daily", value: 1 },
  { label: "Weekly", value: 7 },
  { label: "Monthly", value: 30 },
] as const;

interface FrequencyPickerProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
}

export function FrequencyPicker({ value, onChange, className }: FrequencyPickerProps) {
  const isPreset = PRESETS.some((p) => p.value === value);
  const [customActive, setCustomActive] = useState(!isPreset);
  const [customValue, setCustomValue] = useState(isPreset ? "" : String(value));

  useEffect(() => {
    const matched = PRESETS.some((p) => p.value === value);
    if (matched) {
      setCustomActive(false);
    }
  }, [value]);

  const selectPreset = (presetValue: number) => {
    setCustomActive(false);
    onChange(presetValue);
  };

  const activateCustom = () => {
    setCustomActive(true);
    const num = parseInt(customValue, 10);
    if (num > 0) {
      onChange(num);
    }
  };

  const handleCustomChange = (val: string) => {
    setCustomValue(val);
    const num = parseInt(val, 10);
    if (num > 0) {
      onChange(num);
    }
  };

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {PRESETS.map((preset) => (
        <button
          key={preset.label}
          type="button"
          onClick={() => selectPreset(preset.value)}
          className={cn(
            "px-4 py-1.5 rounded-full text-sm font-semibold",
            "transition-all duration-200 cursor-pointer",
            "hover:scale-105 active:scale-95",
            !customActive && value === preset.value
              ? "bg-app-primary text-white shadow-md"
              : "bg-app-surface-raised text-app-text hover:bg-app-primary/10",
          )}
        >
          {preset.label}
        </button>
      ))}

      <button
        type="button"
        onClick={activateCustom}
        className={cn(
          "px-4 py-1.5 rounded-full text-sm font-semibold",
          "transition-all duration-200 cursor-pointer",
          "hover:scale-105 active:scale-95",
          customActive
            ? "bg-app-primary text-white shadow-md"
            : "bg-app-surface-raised text-app-text hover:bg-app-primary/10",
        )}
      >
        Custom
      </button>

      {customActive && (
        <div className="flex items-center gap-1.5 animate-in fade-in-0 slide-in-from-left-2">
          <Input
            type="number"
            min={1}
            value={customValue}
            onChange={(e) => handleCustomChange(e.target.value)}
            placeholder="days"
            className="w-20 h-8 rounded-full text-center text-sm"
          />
          <span className="text-info text-app-text-muted">days</span>
        </div>
      )}
    </div>
  );
}
